import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Percent } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import UpsellTemplatesManager from './UpsellTemplatesManager';
import RecurringTiersEditor from './RecurringTiersEditor';
import ProductTiersEditor from './ProductTiersEditor';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ShopSettingsTab = ({ recurringDiscount, setRecurringDiscount, creditPackageDiscount, setCreditPackageDiscount }) => {
  const [saving, setSaving] = React.useState(false);
  const [tiers, setTiers] = React.useState([]);
  const [basePrice, setBasePrice] = React.useState(5);
  const [tiersActive, setTiersActive] = React.useState(true);
  const [tiersLoading, setTiersLoading] = React.useState(true);

  // Service-Abo config
  const [aboServices, setAboServices] = React.useState([]);
  const [aboTiers, setAboTiers] = React.useState([]);
  const [aboActive, setAboActive] = React.useState(true);
  const [aboLoading, setAboLoading] = React.useState(true);
  const [savingAbo, setSavingAbo] = React.useState(false);

  // Subscription Volume Tiers (deprecated — eine einzige Tabelle nutzt jetzt
  // den Service-Abo-Kalkulator). Editor-Block entfernt; Werte werden aus
  // `service_abo_config.discount_tiers` gelesen.

  // Upsell-Push config
  const [upsellPush, setUpsellPush] = React.useState(null);
  const [savingUpsell, setSavingUpsell] = React.useState(false);
  const [upsellLoading, setUpsellLoading] = React.useState(true);
  // Vorteils-Art: 'discount' (Rabatt %) oder 'bonus' (🎁 Gratis-Bonuspunkte %)
  const [pricingMode, setPricingMode] = React.useState('discount');

  // Load volume discount tiers
  React.useEffect(() => {
    const loadTiers = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API}/admin/shop/volume-tiers`, { headers: { Authorization: `Bearer ${token}` } });
        setTiers(res.data.tiers || []);
        setBasePrice(res.data.base_price_per_point || 5);
        setTiersActive(res.data.is_active !== false);
        setPricingMode(res.data.pricing_mode || 'discount');
      } catch (e) {
        console.error(e);
      } finally {
        setTiersLoading(false);
      }
    };
    loadTiers();
  }, []);

  // Load service-abo config
  React.useEffect(() => {
    const loadAbo = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API}/admin/shop/service-abo-config`, { headers: { Authorization: `Bearer ${token}` } });
        setAboServices(res.data.services || []);
        setAboTiers(res.data.discount_tiers || []);
        setAboActive(res.data.is_active !== false);
      } catch (e) {
        console.error(e);
      } finally {
        setAboLoading(false);
      }
    };
    loadAbo();
  }, []);

  // Subscription-Volume-Tiers loader/saver entfernt — Mengenrabatte werden
  // jetzt aus `service_abo_config` gelesen (Single Source of Truth).

  // Load Upsell-Push config
  React.useEffect(() => {
    const loadUpsell = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API}/admin/shop/upsell-push`, { headers: { Authorization: `Bearer ${token}` } });
        setUpsellPush(res.data);
      } catch (e) {
        console.error(e);
      } finally {
        setUpsellLoading(false);
      }
    };
    loadUpsell();
  }, []);

  const saveUpsellPush = async () => {
    try {
      setSavingUpsell(true);
      const token = localStorage.getItem('token');
      await axios.put(`${API}/admin/shop/upsell-push`, {
        enabled: !!upsellPush.enabled,
        min_customer_points: Number(upsellPush.min_customer_points) || 0,
        max_customer_points: Number(upsellPush.max_customer_points) || 50,
        inactive_days: Number(upsellPush.inactive_days) || 7,
        schedule_day: Number(upsellPush.schedule_day),
        schedule_hour: Number(upsellPush.schedule_hour),
        title_template: upsellPush.title_template || '',
        body_template: upsellPush.body_template || '',
        only_if_upsell_available: !!upsellPush.only_if_upsell_available,
        use_templates: upsellPush.use_templates !== false,
        email_fallback_enabled: upsellPush.email_fallback_enabled !== false,
        whatsapp_fallback_enabled: upsellPush.whatsapp_fallback_enabled !== false,
        whatsapp_delay_hours: Number(upsellPush.whatsapp_delay_hours) || 24,
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Upsell-Push gespeichert');
    } catch (e) {
      toast.error('Fehler beim Speichern');
    } finally {
      setSavingUpsell(false);
    }
  };

  const updateUpsell = (field, value) => {
    setUpsellPush(prev => ({ ...prev, [field]: value }));
  };

  const updateAboService = (idx, field, value) => {
    const updated = [...aboServices];
    updated[idx] = { ...updated[idx], [field]: value };
    setAboServices(updated);
  };

  // Live-Validierung der Punkte-Stufen: leere Bereiche (Bis < Von),
  // Lücken (Punkte ohne Vorteil) und Überschneidungen sofort anzeigen.
  const tierIssues = (() => {
    const issues = [];
    const sorted = tiers
      .map((t, idx) => ({ ...t, _idx: idx, min: Number(t.min_points) || 0, max: t.max_points != null && t.max_points !== '' ? Number(t.max_points) : null }))
      .sort((a, b) => a.min - b.min);
    sorted.forEach((t, i) => {
      const label = t.label || `Stufe ${t._idx + 1}`;
      if (t.max != null && t.max < t.min) {
        issues.push(`⛔ „${label}": Bis (${t.max}) ist kleiner als Von (${t.min}) — diese Stufe greift NIE.`);
      }
      const next = sorted[i + 1];
      if (next) {
        const effMax = t.max != null && t.max >= t.min ? t.max : t.min;
        if (next.min > effMax + 1) {
          issues.push(`⚠️ Lücke: Käufe von ${effMax + 1}–${next.min - 1} Punkten erhalten KEINEN Vorteil (zwischen „${label}" und „${next.label || 'nächster Stufe'}").`);
        } else if (t.max != null && next.min <= t.max) {
          issues.push(`⚠️ Überschneidung: „${label}" (bis ${t.max}) und „${next.label || 'nächste Stufe'}" (ab ${next.min}).`);
        }
      }
    });
    return issues;
  })();

  const updateAboTier = (idx, field, value) => {
    const updated = [...aboTiers];
    updated[idx] = { ...updated[idx], [field]: value };
    setAboTiers(updated);
  };

  const addAboTier = () => {
    const last = aboTiers[aboTiers.length - 1];
    setAboTiers([...aboTiers, {
      min_points: last ? (last.max_points || last.min_points) + 1 : 1,
      max_points: null,
      discount_percent: 0,
      label: '',
    }]);
  };

  const removeAboTier = (idx) => {
    setAboTiers(aboTiers.filter((_, i) => i !== idx));
  };

  const saveAboConfig = async () => {
    try {
      setSavingAbo(true);
      const token = localStorage.getItem('token');
      await axios.put(`${API}/admin/shop/service-abo-config`, {
        is_active: aboActive,
        services: aboServices.map(s => ({
          id: s.id,
          name: s.name,
          price_per_unit: Number(s.price_per_unit) || 0,
          show_in_shop: s.show_in_shop !== false,
        })),
        discount_tiers: aboTiers.map(t => ({
          min_points: Number(t.min_points),
          max_points: t.max_points ? Number(t.max_points) : null,
          discount_percent: Number(t.discount_percent),
          label: t.label || '',
        })),
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Service-Abo Konfiguration gespeichert');
    } catch (e) {
      toast.error('Fehler beim Speichern');
    } finally {
      setSavingAbo(false);
    }
  };

  const saveTiers = async () => {
    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      await axios.put(`${API}/admin/shop/volume-tiers`, {
        tiers: tiers.map(t => ({
          min_points: Number(t.min_points),
          max_points: t.max_points ? Number(t.max_points) : null,
          discount_percent: Number(t.discount_percent),
          label: t.label || '',
        })),
        base_price_per_point: Number(basePrice),
        is_active: tiersActive,
        pricing_mode: pricingMode,
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Staffelrabatte gespeichert');
    } catch (e) {
      toast.error('Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const addTier = () => {
    const lastTier = tiers[tiers.length - 1];
    setTiers([...tiers, {
      min_points: lastTier ? (lastTier.max_points || lastTier.min_points) + 1 : 10,
      max_points: null,
      discount_percent: 0,
      label: '',
    }]);
  };

  const removeTier = (index) => {
    setTiers(tiers.filter((_, i) => i !== index));
  };

  const updateTier = (index, field, value) => {
    const updated = [...tiers];
    updated[index] = { ...updated[index], [field]: value };
    setTiers(updated);
  };

  const saveSettings = async () => {
    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      await axios.put(`${API}/settings/shop`, {
        recurring_subscription_discount: recurringDiscount,
        credit_package_discount: creditPackageDiscount
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Shop-Einstellungen gespeichert');
    } catch (error) {
      toast.error('Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
    <Card>
      <CardHeader>
        <CardTitle>Shop-Einstellungen</CardTitle>
        <CardDescription>Konfigurieren Sie die Einstellungen für Ihren Kunden-Shop</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <Percent className="w-6 h-6 text-blue-600 shrink-0 mt-0.5" />
            <div className="flex-1">
              <h4 className="font-semibold text-blue-800">Rabatt für 10er-Abos (Einmalkauf)</h4>
              <p className="text-sm text-blue-700 mb-4">Dieser Rabatt wird auf 10er-Abos angewendet. Setze 0% für keinen Rabatt.</p>
              <div className="flex items-center gap-2">
                <Input type="number" min="0" max="100" value={creditPackageDiscount} onChange={(e) => setCreditPackageDiscount(parseInt(e.target.value) || 0)} className="w-24" data-testid="credit-package-discount-input" />
                <span className="text-gray-600 font-medium">%</span>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <Percent className="w-6 h-6 text-green-600 shrink-0 mt-0.5" />
            <div className="flex-1">
              <h4 className="font-semibold text-green-800">Rabatt für wiederkehrende Abos</h4>
              <p className="text-sm text-green-700 mb-4">Kunden sehen diesen Rabatt im Shop und werden ermutigt, ein Abo abzuschliessen.</p>
              <div className="flex items-center gap-2">
                <Input type="number" min="0" max="100" value={recurringDiscount} onChange={(e) => setRecurringDiscount(parseInt(e.target.value) || 0)} className="w-24" data-testid="recurring-discount-input" />
                <span className="text-gray-600 font-medium">%</span>
              </div>
              <p className="text-xs text-gray-500 mt-2">Empfohlen: 5-10%</p>
            </div>
          </div>
        </div>

        <Button onClick={saveSettings} disabled={saving} className="w-full" data-testid="save-shop-settings-btn">
          {saving ? 'Speichern...' : 'Einstellungen speichern'}
        </Button>

        <div className="border rounded-lg p-4">
          <h4 className="font-medium mb-3">Vorschau im Kunden-Shop</h4>
          <div className="space-y-3">
            <div className="bg-gray-50 rounded p-4">
              <div className="flex items-center justify-between">
                <div>
                  <Badge variant="outline" className="mr-2 text-blue-600 border-blue-600">10er-Abo</Badge>
                  <span className="font-medium">10x Personal Training</span>
                </div>
                <div className="text-right">
                  {creditPackageDiscount > 0 && (
                    <>
                      <span className="text-gray-400 line-through text-sm mr-2">CHF 750.00</span>
                      <Badge className="bg-blue-500 text-white mr-2">-{creditPackageDiscount}%</Badge>
                    </>
                  )}
                  <span className={`font-bold ${creditPackageDiscount > 0 ? 'text-blue-600' : ''}`}>
                    CHF {(750 * (1 - creditPackageDiscount / 100)).toFixed(2)}
                  </span>
                </div>
              </div>
            </div>
            <div className="bg-gray-50 rounded p-4">
              <div className="flex items-center justify-between">
                <div>
                  <Badge className="mr-2 bg-green-500 text-white">ABO</Badge>
                  <span className="font-medium">10x EMS Training / Monat</span>
                </div>
                <div className="text-right">
                  {recurringDiscount > 0 && (
                    <>
                      <span className="text-gray-400 line-through text-sm mr-2">CHF 500.00</span>
                      <Badge className="bg-green-500 text-white mr-2">-{recurringDiscount}%</Badge>
                    </>
                  )}
                  <span className={`font-bold ${recurringDiscount > 0 ? 'text-green-600' : ''}`}>
                    CHF {(500 * (1 - recurringDiscount / 100)).toFixed(2)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <h4 className="font-medium text-yellow-800 mb-2">Unterschied zwischen 10er-Abos und Wiederkehrenden Abos</h4>
          <div className="text-sm text-yellow-700 space-y-2">
            <p><strong>10er-Abos (Einmalkauf):</strong> Kunden kaufen ein Service-Paket einmalig. Keine automatische Verlängerung.</p>
            <p><strong>Wiederkehrende Abos:</strong> Automatische Verlängerung wenn Credits aufgebraucht sind. Kündigung nur durch Admin.</p>
          </div>
        </div>

        {/* Hinweis: Mengenrabatte werden zentral im "Service-Abo Kalkulator"
            weiter unten gepflegt — diese gelten dann automatisch auch für
            fertige Abo-Vorlagen (Single Source of Truth). */}

        {/* Volume Discount Tiers */}
        <div className="border rounded-lg p-4 space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="font-semibold">Staffelrabatte (Punkte-Kalkulator)</h4>
              <p className="text-sm text-gray-500">Kunden sehen im Shop einen Kalkulator mit Mengenrabatt</p>
            </div>
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={tiersActive} onChange={e => setTiersActive(e.target.checked)} className="rounded" />
              <span className="text-sm">Aktiv</span>
            </label>
          </div>

          <div className="flex items-center gap-3">
            <label className="text-sm font-medium">Grundpreis pro Punkt (CHF):</label>
            <Input
              type="number" step="0.5" min="0.5"
              value={basePrice}
              onChange={e => setBasePrice(e.target.value)}
              className="w-24"
            />
          </div>

          {/* Vorteils-Art: Rabatt oder Bonuspunkte */}
          <div className="flex items-center gap-3">
            <label className="text-sm font-medium">Vorteils-Art:</label>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setPricingMode('discount')}
                className={`px-3 py-1.5 rounded-lg border-2 text-sm transition ${pricingMode === 'discount' ? 'border-blue-500 bg-blue-50 text-blue-700 font-medium' : 'border-gray-200 text-gray-500 hover:bg-gray-50'}`}
                data-testid="pricing-mode-discount"
              >
                💸 Rabatt % (Preis sinkt)
              </button>
              <button
                type="button"
                onClick={() => setPricingMode('bonus')}
                className={`px-3 py-1.5 rounded-lg border-2 text-sm transition ${pricingMode === 'bonus' ? 'border-amber-500 bg-amber-50 text-amber-700 font-medium' : 'border-gray-200 text-gray-500 hover:bg-gray-50'}`}
                data-testid="pricing-mode-bonus"
              >
                🎁 Bonuspunkte gratis (Preis bleibt)
              </button>
            </div>
          </div>
          {pricingMode === 'bonus' && (
            <p className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-lg p-2">
              🎁 Im Bonus-Modus zahlt der Kunde den vollen Preis und erhält den Prozentsatz als <strong>Gratis-Punkte obendrauf</strong>.
              Beispiel: 120 Punkte bei 5% → Kunde erhält 126 Punkte.
            </p>
          )}

          {!tiersLoading && (
            <div className="space-y-2">
              <div className="grid grid-cols-5 gap-2 text-xs font-medium text-gray-500 px-1">
                <span>Label</span>
                <span>Von (Pkt)</span>
                <span>Bis (Pkt)</span>
                <span>{pricingMode === 'bonus' ? '🎁 Bonus %' : 'Rabatt %'}</span>
                <span></span>
              </div>
              {tiers.map((tier, i) => (
                <div key={i} className="grid grid-cols-5 gap-2 items-center">
                  <Input value={tier.label || ''} onChange={e => updateTier(i, 'label', e.target.value)} placeholder="Label" className="text-sm" />
                  <Input type="number" value={tier.min_points} onChange={e => updateTier(i, 'min_points', e.target.value)} className="text-sm" />
                  <Input type="number" value={tier.max_points || ''} onChange={e => updateTier(i, 'max_points', e.target.value || null)} placeholder="unbegrenzt" className="text-sm" />
                  <Input type="number" step="0.5" value={tier.discount_percent} onChange={e => updateTier(i, 'discount_percent', e.target.value)} className="text-sm" />
                  <div className="flex items-center gap-2">
                    {pricingMode === 'bonus' && Number(tier.min_points) > 0 && Number(tier.discount_percent) > 0 && (
                      <span className="text-xs text-amber-600 whitespace-nowrap">z.B. {tier.min_points} → +{Math.round(Number(tier.min_points) * Number(tier.discount_percent) / 100)} gratis</span>
                    )}
                    <Button variant="ghost" size="sm" onClick={() => removeTier(i)} className="text-red-500 hover:text-red-700 text-xs">X</Button>
                  </div>
                </div>
              ))}
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={addTier} data-testid="add-volume-tier-btn">+ Stufe hinzufügen</Button>
                <Button size="sm" onClick={saveTiers} disabled={saving} data-testid="save-volume-tiers-btn">{saving ? 'Speichern...' : 'Staffelrabatte speichern'}</Button>
              </div>
              {tierIssues.length > 0 && (
                <div className="bg-red-50 border border-red-300 rounded-lg p-3 space-y-1" data-testid="tier-validation-warnings">
                  <p className="text-sm font-semibold text-red-700">Stufen-Konfiguration prüfen:</p>
                  {tierIssues.map((msg, i) => (
                    <p key={i} className="text-xs text-red-700">{msg}</p>
                  ))}
                  <p className="text-xs text-red-500 mt-1">Tipp: „Bis" sollte immer <strong>Von der nächsten Stufe − 1</strong> sein (z.B. Von 160 / Bis 319, nächste Stufe Von 320).</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Service-Abo Konfiguration */}
        <div className="border rounded-lg p-4 space-y-4" data-testid="service-abo-config-section">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="font-semibold">Service-Abo Kalkulator</h4>
              <p className="text-sm text-gray-500">Kunden wählen Service + Menge und erhalten Mengenrabatt</p>
            </div>
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={aboActive} onChange={e => setAboActive(e.target.checked)} className="rounded" data-testid="abo-active-checkbox" />
              <span className="text-sm">Aktiv</span>
            </label>
          </div>

          {!aboLoading && (
            <>
              {/* Services Liste */}
              <div className="space-y-2">
                <h5 className="text-sm font-medium text-gray-700">Services (Preis pro Einheit / im Shop zeigen)</h5>
                <div className="grid grid-cols-12 gap-2 text-xs font-medium text-gray-500 px-1">
                  <span className="col-span-6">Service</span>
                  <span className="col-span-3">Preis/Einheit (CHF)</span>
                  <span className="col-span-3">Im Shop</span>
                </div>
                <div className="max-h-72 overflow-y-auto space-y-1 pr-1">
                  {aboServices.map((s, i) => (
                    <div key={s.id || i} className="grid grid-cols-12 gap-2 items-center" data-testid={`abo-service-row-${i}`}>
                      <Input
                        value={s.name || ''}
                        onChange={e => updateAboService(i, 'name', e.target.value)}
                        className="col-span-6 text-sm"
                      />
                      <Input
                        type="number" step="0.5" min="0"
                        value={s.price_per_unit ?? 0}
                        onChange={e => updateAboService(i, 'price_per_unit', e.target.value)}
                        className="col-span-3 text-sm"
                      />
                      <label className="col-span-3 flex items-center gap-2 text-sm">
                        <input
                          type="checkbox"
                          checked={s.show_in_shop !== false}
                          onChange={e => updateAboService(i, 'show_in_shop', e.target.checked)}
                          className="rounded"
                        />
                        sichtbar
                      </label>
                    </div>
                  ))}
                  {aboServices.length === 0 && (
                    <p className="text-sm text-gray-500 italic">Keine Services gefunden. Lege zuerst Services an.</p>
                  )}
                </div>
              </div>

              {/* Staffelrabatte für Service-Abos */}
              <div className="space-y-2 pt-2 border-t">
                <h5 className="text-sm font-medium text-gray-700">Staffelrabatte (Menge)</h5>
                <div className="grid grid-cols-5 gap-2 text-xs font-medium text-gray-500 px-1">
                  <span>Label</span>
                  <span>Von (Menge)</span>
                  <span>Bis (Menge)</span>
                  <span>Rabatt %</span>
                  <span></span>
                </div>
                {aboTiers.map((tier, i) => (
                  <div key={i} className="grid grid-cols-5 gap-2 items-center" data-testid={`abo-tier-row-${i}`}>
                    <Input value={tier.label || ''} onChange={e => updateAboTier(i, 'label', e.target.value)} placeholder="Label" className="text-sm" />
                    <Input type="number" value={tier.min_points} onChange={e => updateAboTier(i, 'min_points', e.target.value)} className="text-sm" />
                    <Input type="number" value={tier.max_points || ''} onChange={e => updateAboTier(i, 'max_points', e.target.value || null)} placeholder="unbegrenzt" className="text-sm" />
                    <Input type="number" step="0.5" value={tier.discount_percent} onChange={e => updateAboTier(i, 'discount_percent', e.target.value)} className="text-sm" />
                    <Button variant="ghost" size="sm" onClick={() => removeAboTier(i)} className="text-red-500 hover:text-red-700 text-xs">X</Button>
                  </div>
                ))}
              </div>

              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={addAboTier} data-testid="add-abo-tier-btn">+ Stufe hinzufügen</Button>
                <Button size="sm" onClick={saveAboConfig} disabled={savingAbo} data-testid="save-abo-config-btn">
                  {savingAbo ? 'Speichern...' : 'Service-Abo Konfiguration speichern'}
                </Button>
              </div>
            </>
          )}
        </div>

        {/* Upsell-Push Benachrichtigungen */}
        <div className="border rounded-lg p-4 space-y-4" data-testid="upsell-push-section">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="font-semibold flex items-center gap-2">
                <Percent className="w-4 h-4 text-amber-500" /> Upsell-Push Benachrichtigungen
              </h4>
              <p className="text-sm text-gray-500">Kunden erinnern: „Mit X Punkten mehr bekommst du Y% Rabatt"</p>
            </div>
            {upsellPush && (
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={!!upsellPush.enabled}
                  onChange={e => updateUpsell('enabled', e.target.checked)}
                  className="rounded"
                  data-testid="upsell-push-enabled"
                />
                <span className="text-sm">Aktiv</span>
              </label>
            )}
          </div>

          {!upsellLoading && upsellPush && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div>
                  <label className="text-xs font-medium text-gray-600 block mb-1">Min. Punkte-Rest</label>
                  <Input
                    type="number" min="0"
                    value={upsellPush.min_customer_points ?? 0}
                    onChange={e => updateUpsell('min_customer_points', e.target.value)}
                    className="text-sm"
                    data-testid="upsell-min-points"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-600 block mb-1">Max. Punkte-Rest</label>
                  <Input
                    type="number" min="0"
                    value={upsellPush.max_customer_points ?? 50}
                    onChange={e => updateUpsell('max_customer_points', e.target.value)}
                    className="text-sm"
                    data-testid="upsell-max-points"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-600 block mb-1">Inaktiv seit (Tage)</label>
                  <Input
                    type="number" min="0"
                    value={upsellPush.inactive_days ?? 7}
                    onChange={e => updateUpsell('inactive_days', e.target.value)}
                    className="text-sm"
                    data-testid="upsell-inactive-days"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-600 block mb-1">Stunde (0-23)</label>
                  <Input
                    type="number" min="0" max="23"
                    value={upsellPush.schedule_hour ?? 10}
                    onChange={e => updateUpsell('schedule_hour', e.target.value)}
                    className="text-sm"
                    data-testid="upsell-hour"
                  />
                </div>
              </div>

              <div>
                <label className="text-xs font-medium text-gray-600 block mb-1">Versand-Tag</label>
                <select
                  value={upsellPush.schedule_day ?? -1}
                  onChange={e => updateUpsell('schedule_day', e.target.value)}
                  className="w-full border rounded-md px-3 py-2 text-sm"
                  data-testid="upsell-day"
                >
                  <option value={-1}>Täglich</option>
                  <option value={0}>Montag</option>
                  <option value={1}>Dienstag</option>
                  <option value={2}>Mittwoch</option>
                  <option value={3}>Donnerstag</option>
                  <option value={4}>Freitag</option>
                  <option value={5}>Samstag</option>
                  <option value={6}>Sonntag</option>
                </select>
              </div>

              <div>
                <label className="text-xs font-medium text-gray-600 block mb-1">Titel-Vorlage</label>
                <Input
                  value={upsellPush.title_template || ''}
                  onChange={e => updateUpsell('title_template', e.target.value)}
                  className="text-sm"
                  data-testid="upsell-title"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 block mb-1">Nachricht-Vorlage</label>
                <textarea
                  value={upsellPush.body_template || ''}
                  onChange={e => updateUpsell('body_template', e.target.value)}
                  rows={3}
                  className="w-full border rounded-md px-3 py-2 text-sm"
                  data-testid="upsell-body"
                />
              </div>

              <div className="text-xs text-gray-500 bg-gray-50 rounded p-3 space-y-1">
                <p className="font-medium text-gray-700">Verfügbare Platzhalter:</p>
                <p><code>{'{first_name}'}</code>, <code>{'{current_points}'}</code>, <code>{'{next_points}'}</code>, <code>{'{next_label}'}</code></p>
                <p><code>{'{extra_points}'}</code>, <code>{'{extra_cost}'}</code>, <code>{'{next_discount_percent}'}</code></p>
              </div>

              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={!!upsellPush.only_if_upsell_available}
                  onChange={e => updateUpsell('only_if_upsell_available', e.target.checked)}
                  className="rounded"
                />
                Nur senden, wenn ein Upsell verfügbar ist
              </label>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-3 border-t">
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={upsellPush.use_templates !== false}
                    onChange={e => updateUpsell('use_templates', e.target.checked)}
                    className="rounded"
                  />
                  A/B-Templates nutzen (wenn vorhanden)
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={upsellPush.email_fallback_enabled !== false}
                    onChange={e => updateUpsell('email_fallback_enabled', e.target.checked)}
                    className="rounded"
                    data-testid="upsell-email-fallback"
                  />
                  E-Mail Fallback (wenn keine App)
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={upsellPush.whatsapp_fallback_enabled !== false}
                    onChange={e => updateUpsell('whatsapp_fallback_enabled', e.target.checked)}
                    className="rounded"
                    data-testid="upsell-whatsapp-fallback"
                  />
                  WhatsApp Fallback (zeitversetzt)
                </label>
                <div>
                  <label className="text-xs font-medium text-gray-600 block mb-1">WhatsApp-Verzögerung (Std.)</label>
                  <Input
                    type="number" min="1" max="168"
                    value={upsellPush.whatsapp_delay_hours ?? 24}
                    onChange={e => updateUpsell('whatsapp_delay_hours', e.target.value)}
                    className="text-sm w-32"
                    data-testid="upsell-whatsapp-delay"
                  />
                </div>
              </div>

              <Button onClick={saveUpsellPush} disabled={savingUpsell} data-testid="save-upsell-push-btn">
                {savingUpsell ? 'Speichern...' : 'Upsell-Push Einstellungen speichern'}
              </Button>

              {/* A/B Templates Manager */}
              <div className="pt-4 border-t mt-4">
                <UpsellTemplatesManager />
              </div>
            </>
          )}
        </div>
      </CardContent>
    </Card>

    {/* Wiederkehrende Abos · Vertragslaufzeit-Staffelung (NEU 07.05.2026) */}
    <RecurringTiersEditor />

    {/* Produkte · Mengen-Staffelung (NEU 07.05.2026) */}
    <ProductTiersEditor />
    </>
  );
};

export default ShopSettingsTab;
